/**
 * `TemplatePickerModal` — テンプレートメッセージを 1 件選択するためのポップアップ
 * ダイアログ。ChatView のハンバーガーメニュー「テンプレート」項目から開かれる。
 *
 * 設計方針:
 *   - 表示は native `<dialog>` 要素を使用 (IconSettingsModal と同様の方式)。
 *     `showModal()` でフォーカストラップとバックドロップが OS / ブラウザの
 *     既定挙動で得られる。
 *   - 選択時は `onSelect(template)` を呼び出すだけ。テキストエリアへの挿入
 *     は呼び出し元 (ChatView → MessageInput) の責務。
 *   - テンプレートが 0 件のときはそもそも開かない想定だが、念のため空表示も
 *     描画できるようにしてある。
 *   - 半透明 / ぼかしは一切使わず、`--popup-bg` 等のテーマトークンで完全
 *     不透明に描画する (ポップアップは確実に背景を遮蔽する方針)。
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties, JSX } from 'react';

export interface TemplatePickerModalProps {
  /** 表示するテンプレート文字列の配列。空配列のときは「未登録」表示。 */
  readonly templates: readonly string[];
  /** テンプレートが 1 件選択されたときのハンドラ。 */
  readonly onSelect: (template: string) => void;
  /** モーダルを閉じる要求。ESC / バックドロップクリック / 「閉じる」ボタン。 */
  readonly onClose: () => void;
}

export function TemplatePickerModal(
  props: TemplatePickerModalProps,
): JSX.Element {
  const { templates, onSelect, onClose } = props;
  const dialogRef = useRef<HTMLDialogElement>(null);

  // マウント時にモーダル化、アンマウント時に閉じる。IconSettingsModal と同じ
  // ライフサイクル管理。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) {
      dialog.showModal();
    }
    return () => {
      if (dialog && dialog.open) {
        dialog.close();
      }
    };
  }, []);

  // ESC キーの cancel イベント + バックドロップクリックを onClose に変換。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const handleCancel = (e: Event) => {
      e.preventDefault();
      onClose();
    };

    const handleClick = (e: MouseEvent) => {
      // <dialog> 自身がクリックされた = バックドロップクリック
      if (e.target === dialog) {
        onClose();
      }
    };

    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleClick);
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleClick);
    };
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      className="template-picker-modal"
      aria-label="テンプレートメッセージを選択"
    >
      <div className="template-picker-modal__content">
        <h2 className="template-picker-modal__title">
          テンプレートメッセージ
        </h2>
        <p className="template-picker-modal__desc">
          挿入したいテンプレートをタップしてください。
        </p>

        {templates.length === 0 ? (
          <p className="template-picker-modal__empty" role="status">
            登録されたテンプレートはありません。ダッシュボードから追加できます。
          </p>
        ) : (
          <ul className="template-picker-modal__list" role="listbox">
            {templates.map((tpl, idx) => (
              <li key={`${idx}-${tpl}`} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={false}
                  className="template-picker-modal__item"
                  onClick={() => onSelect(tpl)}
                  title="クリックでメッセージ欄に挿入"
                >
                  <span
                    className="template-picker-modal__item-badge"
                    aria-hidden="true"
                  >
                    {idx + 1}
                  </span>
                  <span
                    className="template-picker-modal__item-body"
                    style={preWrapStyle}
                  >
                    {tpl}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="template-picker-modal__actions">
          <button
            type="button"
            className="template-picker-modal__close"
            onClick={onClose}
          >
            閉じる
          </button>
        </div>
      </div>
    </dialog>
  );
}

// 改行を含む複数行テンプレートをそのまま見せるための pre-wrap。
const preWrapStyle: CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};
